import { PaletteMode, PaletteOptions } from '@mui/material';
import { GetThemeOptions } from '../theme';
import { unitySemanticDarkColorTokens, unitySemanticLightColorTokens } from '../unityTokens';
import { grey } from './colors';
import { buildPaletteFromMetadata, PaletteTokenMetadata, Token } from './panw-palette/token-metadata';

/** For use strictly within @chronosphereio/chrono-ui */
const chipTokens = [
  // Clickable / removable - default state
  { propertyPath: 'clickable.default.background', tokenName: '--semantic-bg-interactive-neutral-default' },
  { propertyPath: 'clickable.default.border', tokenName: '--semantic-border-bold' },
  { propertyPath: 'clickable.default.text', tokenName: '--semantic-text-default' },
  { propertyPath: 'clickable.default.hover', tokenName: '--semantic-bg-interactive-neutral-hover' },

  // Clickable / removable - disabled state
  { propertyPath: 'clickable.disabled.text', tokenName: '--semantic-text-disabled' },

  // Selectable - unselected
  { propertyPath: 'selectable.unselected.border', tokenName: '--semantic-border-bold' },
  { propertyPath: 'selectable.unselected.text', tokenName: '--semantic-text-default' },
  { propertyPath: 'selectable.unselected.hover', tokenName: '--semantic-bg-interactive-neutral-hover' },

  // Selectable - unselected disabled
  { propertyPath: 'selectable.unselectedDisabled.border', tokenName: '--semantic-border-default' },
  { propertyPath: 'selectable.unselectedDisabled.text', tokenName: '--semantic-text-disabled' },

  // Selectable - selected
  { propertyPath: 'selectable.selected.background', tokenName: '--semantic-bg-interactive-highlight-default' },
  { propertyPath: 'selectable.selected.border', tokenName: '--semantic-border-bold' },
  { propertyPath: 'selectable.selected.text', tokenName: '--semantic-text-inverse' },
  { propertyPath: 'selectable.selected.hover', tokenName: '--semantic-bg-interactive-highlight-hover' },

  // Selectable - selected disabled
  { propertyPath: 'selectable.selectedDisabled.background', tokenName: '--semantic-bg-interactive-neutral-default' },
  { propertyPath: 'selectable.selectedDisabled.text', tokenName: '--semantic-text-disabled' },
] satisfies Token[];

export const chipTokenMetadata: PaletteTokenMetadata = {
  componentName: 'chip',
  tokens: chipTokens,
};

export const chip = (mode: PaletteMode, opts: GetThemeOptions): PaletteOptions['chip'] => {
  if (opts.panwRebrandStubYear) {
    const tokens = mode === 'light' ? unitySemanticLightColorTokens : unitySemanticDarkColorTokens;
    return buildPaletteFromMetadata(chipTokenMetadata.tokens, tokens);
  }

  // only `outlined.default.border` and `outlined.default.hover` are used by chip-overrides.
  // everything else is just here for backwards compatibility
  return mode === 'light'
    ? {
        clickable: {
          default: {
            background: grey[150],
            border: grey[50],
            text: grey[950],
            hover: grey[100],
          },
          disabled: {
            text: grey[300],
          },
        },
        selectable: {
          unselected: {
            border: grey[150],
            text: grey[600],
            hover: grey[50],
          },
          unselectedDisabled: {
            border: grey[150],
            text: grey[300],
          },
          selected: {
            background: grey[150],
            border: grey[50],
            text: grey[950],
            hover: grey[100],
          },
          selectedDisabled: {
            background: grey[150],
            text: grey[300],
          },
        },
      }
    : {
        clickable: {
          default: {
            background: grey[300],
            border: grey[300],
            text: grey[950],
            hover: grey[800],
          },
          disabled: {
            text: grey[500],
          },
        },
        selectable: {
          unselected: {
            border: grey[850],
            text: grey[200],
            hover: grey[800],
          },
          unselectedDisabled: {
            border: grey[850],
            text: grey[500],
          },
          selected: {
            background: grey[300],
            border: grey[300],
            text: grey[950],
            hover: grey[800],
          },
          selectedDisabled: {
            background: grey[300],
            text: grey[500],
          },
        },
      };
};
